package com.macrofocus.eflux.ui

import com.macrofocus.eflux.EFluxConfiguration
import com.macrofocus.eflux.ViewLoader
import com.macrofocus.eflux.network.EFluxLink
import com.macrofocus.eflux.network.LinkField
import com.macrofocus.eflux.ui.heatmap.createLinkHeatmapData
import deckgl.DeckGL
import deckgl.HeatmapLayer
import deckgl.HeatmapLayerProps
import deckgl.OrthographicView
import emotion.react.css
import js.array.ReadonlyArray
import js.coroutines.awaitCancellation
import js.objects.unsafeJso
import org.mkui.react.useProperty
import react.*
import react.dom.html.ReactHTML
import web.cssom.Position
import web.cssom.pct
import web.html.HTMLDivElement
import web.resize.ResizeObserver
import web.resize.ResizeObserverEntry
import web.svg.SVGGeometryElement
import kotlin.math.log2
import kotlin.math.min

external interface HeatmapProps : Props {
    var configuration: EFluxConfiguration
    var viewLoader: ViewLoader
    var show: Boolean
    var worldX : Double
    var worldY : Double
    var worldWidth : Double
    var worldHeight : Double
}

public val Heatmap = FC<HeatmapProps> { props ->
    if(!props.show) {
        return@FC
    }

    val (onPanZoom, _) = useProperty(props.viewLoader.svgMatrix)

    val (backgroundLinks, _) = useProperty(props.configuration.heatmapLinks)
    val (getNetwork, _) = useProperty(props.viewLoader.efluxNetwork)

    val screenScaleX : Double
    val screenTransX : Double
    val screenTransY : Double
    if(onPanZoom != null) {
        screenScaleX = onPanZoom.a
        screenTransX = onPanZoom.e
        screenTransY = onPanZoom.f
    } else {
        screenScaleX = 1.0
        screenTransX = 0.0
        screenTransY = 0.0
    }

    val views = OrthographicView()
    val (screenWidth, setScreenWidth) = useState { 1600 }
    val (screenHeight, setScreenHeight) = useState { 1200 }

    val worldX = props.worldX
    val worldY = props.worldY
    val worldWidth = props.worldWidth - worldX
    val worldHeight = props.worldHeight - worldY

    val world2pixelRatio = min(screenWidth / worldWidth, screenHeight / worldHeight)

    val actualWorldWidth = screenWidth / world2pixelRatio
    val actualWorldHeight = screenHeight / world2pixelRatio

    val aspectScale = world2pixelRatio * screenScaleX
    val zoom = log2(aspectScale)

    val worldShiftX = (actualWorldWidth - worldWidth) / 2
    val worldShiftY = (actualWorldHeight - worldHeight) / 2

    val screenToWorldX = { it : Double ->
        ((it / screenScaleX) * actualWorldWidth / screenWidth) - worldShiftX
    }

    val screenToWorldY = { it : Double ->
        ((it / screenScaleX) * actualWorldHeight / screenHeight) - worldShiftY
    }

    val worldX1 = screenToWorldX(-screenTransX)
    val worldY1 = screenToWorldY(-screenTransY)

    val worldX2 = screenToWorldX(-screenTransX + screenWidth.toDouble())
    val worldY2 = screenToWorldY(-screenTransY + screenHeight.toDouble())

    val worldMidX = (worldX1 + worldX2) / 2
    val worldMidY = (worldY1 + worldY2) / 2

    val INITIAL_VIEW_STATE : dynamic = unsafeJso {
        target = arrayOf(worldMidX, worldMidY, 0)
        this.zoom = zoom
    }

    val normalized = backgroundLinks == LinkField.va || backgroundLinks == LinkField.vm ||  backgroundLinks == LinkField.dva || backgroundLinks == LinkField.dvm
    val negative = backgroundLinks == LinkField.va || backgroundLinks == LinkField.vm
    val clearSplitAtZero = false // backgroundLinks == LinkField.vm || backgroundLinks == LinkField.dvm
    val relative = backgroundLinks == LinkField.dva || backgroundLinks == LinkField.dvm
    val intensity = when(backgroundLinks) {
        LinkField.va -> 1.5
        LinkField.dva -> 10
        LinkField.vm -> 1
        LinkField.dvm -> 10
        else -> 1
    }

    val (data, setData) = useState(null as Array<dynamic>?)
    useEffect(backgroundLinks, getNetwork) {
        val array = createLinkHeatmapData(getNetwork?.links, backgroundLinks, { link -> link.unsafeCast<EFluxLink>().elements.isNotEmpty() },
            { link, list, startAmount, endAmount ->
            val svgElement = link.unsafeCast<EFluxLink>().elements[0]
            // Visibility is testable by checking if any of the element's nodes have an offsetWidth, offsetHeight, or bounding boxes (getClientRects()).
            if(svgElement is SVGGeometryElement && svgElement.getClientRects().length > 0) {
                val totalLength = svgElement.getTotalLength().toInt()
                for (i in 0..totalLength) {
                    val point = svgElement.getPointAtLength(i.toFloat())
                    val amount = (((totalLength - 1 - i) * startAmount) + (i * endAmount)) / (totalLength - 1)
                    val p = unsafeJso<dynamic> {
                        COORDINATES = arrayOf(point.x, point.y)
                        WEIGHT = amount
                    }
                    list.add(p)
                }
            }
        }, normalized, relative)
        setData(array)
    }

    val RdYlBu = arrayOf(
        arrayOf(165,0,38),
        arrayOf(215,48,39),
        arrayOf(244,109,67),
        arrayOf(253,174,97),
        arrayOf(254,224,144),
        arrayOf(255,255,191, 0),
        arrayOf(224,243,248),
        arrayOf(171,217,233),
        arrayOf(116,173,209),
        arrayOf(69,117,180),
        arrayOf(49,54,149)
    ).reversedArray()

    val RdGy = arrayOf(
        arrayOf(103,0,31),
        arrayOf(178,24,43),
        arrayOf(214,96,77),
        arrayOf(244,165,130),
        arrayOf(253,219,199),
        arrayOf(255,255,255, 127),
        arrayOf(224,224,224),
        arrayOf(186,186,186),
        arrayOf(135,135,135),
        arrayOf(77,77,77),
        arrayOf(26,26,26)
    )

    val neg: Array<Array<Int>>
    val pos: Array<Array<Int>>
    if(clearSplitAtZero) {
        neg = RdYlBu.sliceArray(0..4).reversedArray()
        pos = RdYlBu.sliceArray(6..10)
    } else {
        neg = RdYlBu.sliceArray(0..5).reversedArray()
        pos = RdYlBu.sliceArray(5..10)
    }

    val negLayer = HeatmapLayer(unsafeJso<HeatmapLayerProps<Any?>> {
        id = "negLayer"
        this.data = data
        colorRange = neg
        if(normalized) {
            colorDomain = arrayOf(0, 1.0)
        }

        getPosition = { d : dynamic -> d.COORDINATES }
        if(normalized) {
            getWeight = { d: dynamic -> if(d != null) 1.0 - (2.0 * (d.WEIGHT as Double)) else null }
        } else {
            getWeight = { d: dynamic -> d.WEIGHT }
        }
        aggregation = "MEAN"
        threshold = 0
        this.intensity = intensity
        radiusPixels = 20 + (10 * screenScaleX)
    })

    val posLayer = HeatmapLayer(unsafeJso<HeatmapLayerProps<Any?>> {
        id = "posLayer"
        this.data = data
        colorRange = pos
        if(normalized) {
            colorDomain = arrayOf(0, 1.0)
        }

        getPosition = { d : dynamic -> d.COORDINATES }
        if(normalized) {
            getWeight = { d : dynamic -> if(d != null) -1.0 + (2.0 * (d.WEIGHT as Double)) else null }
        } else {
            getWeight = { d: dynamic -> d.WEIGHT }
        }
        aggregation = "MEAN"
        threshold = 0
        this.intensity = intensity
        radiusPixels = 20 + (10 * screenScaleX)
    })

    val heatmapLayer = HeatmapLayer(unsafeJso<HeatmapLayerProps<Any?>> {
        id = "heatmapLayer"
        this.data = data
        if(backgroundLinks == LinkField.dva) {
            colorRange = RdGy
  //          colorDomain = arrayOf(-180, 180)
        } else if(backgroundLinks == LinkField.dvm) {
            colorRange = RdGy
            colorDomain = arrayOf(0, 1.0)
        }

        getPosition = { d : dynamic -> d.COORDINATES }
        getWeight = { d : dynamic -> d.WEIGHT }
        aggregation = "MEAN"
    })

    val targetRef = useRef<HTMLDivElement>()

    ReactHTML.div {
        ref = targetRef

        css {
            position = Position.absolute
            this.width = 100.pct
            this.height = 100.pct
        }



        DeckGL {
            asDynamic().glOptions = unsafeJso {
                preserveDrawingBuffer = true
            }

            this.views = views
            initialViewState = INITIAL_VIEW_STATE
            controller = false
            layers = if(negative) arrayOf( negLayer, posLayer) else arrayOf( posLayer)
        }
    }

    useEffectOnce {
        val div = targetRef.current
        if (div != null) {
            val resizeObserver = ResizeObserver { entries, _ -> resize(entries, setScreenWidth, setScreenHeight) }
            resizeObserver.observe(div)

            awaitCancellation {
                resizeObserver.unobserve(div)
                resizeObserver.disconnect()
            }
        }
    }
}

internal fun resize(entries: ReadonlyArray<ResizeObserverEntry>, setWidth: StateSetter<Int>, setHeight: StateSetter<Int>) {
    val width = entries[0].target.clientWidth // Todo: offsetWidth
    val height = entries[0].target.clientHeight

    println("Resizing $width x $height")

    setWidth(width)
    setHeight(height)
}